import { AppState, AppAction, Household, DataScope, TemplateType, CalendarEvent, SubscriptionStatus } from '../types';

/**
 * 🏁 INITIAL STATE
 * Matches the consolidated AppState interface.
 * activeDataScope defaults to 'PERSONAL' to ensure privacy-first boot sequences.
 */
export const initialState: AppState = {
  subscriptionStatus: null,
  currentUser: null,
  households: [],
  currentHouseholdId: typeof window !== 'undefined' ? localStorage.getItem('everyday-os-household-id') : null,
  // 🛡️ Hybrid Scoping State
  activeDataScope: (typeof window !== 'undefined' ? localStorage.getItem('everyday-os-active-scope') as DataScope : 'PERSONAL') || 'PERSONAL',
  tasks: [],
  userProfiles: [],
  personalEvents: [], // Renamed from calendarEvents
  householdEvents: [], // Added
  customEvents: [], // Added
  inventory: [],
  contacts: [],
  maintenanceTasks: [],
  notifications: [],
  theme: 'dark',
  dailySpark: null,
  isBooting: true,
  lastSyncTimestamp: Date.now(),
  intelligence: {
    sparks: []
  },
  auditLogs: [],
  dashboardLayout: null,
  customGroceryCategories: {
    personal: {},
    household: {},
  },
  emailVerified: true,
};

/**
 * 🧠 MASTER APP REDUCER
 * Orchestrates Hybrid Scoping: /users/{uid} vs /households/{hid}.
 */
export const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    // --- 👤 IDENTITY & AUTH ---
    case 'SET_USER':
      return {
        ...state,
        currentUser: action.payload
      };

    case 'UPDATE_USER':
      return {
        ...state,
        currentUser: state.currentUser
          ? { ...state.currentUser, ...action.payload }
          : null
      };

    // --- 🛰️ HOUSEHOLD & TENANCY MANAGEMENT ---
    case 'SET_HOUSEHOLDS':
      return {
        ...state,
        households: action.payload
      };

    case 'SET_CURRENT_HOUSEHOLD': {
      /**
       * 🛡️ HYBRID SCOPING STRATEGY
       * When switching context, we must evaluate the validity of the current scope.
       * If moving to null (Personal Mode), scope MUST be 'PERSONAL'.
       */
      const nextHouseholdId = action.payload;
      // If switching to a non-household context, force scope to PERSONAL
      const nextScope: DataScope = nextHouseholdId ? state.activeDataScope : 'PERSONAL';

      if (typeof window !== 'undefined') {
        if (nextHouseholdId) {
          localStorage.setItem('everyday-os-household-id', nextHouseholdId);
        } else {
          localStorage.removeItem('everyday-os-household-id');
          localStorage.setItem('everyday-os-active-scope', 'PERSONAL'); // Always set personal mode for no household
        }
        // Also persist the current data scope for the next session
        localStorage.setItem('everyday-os-active-scope', nextScope);
      }

      return {
        ...state,
        currentHouseholdId: nextHouseholdId,
        activeDataScope: nextScope,
        // 🧹 Privacy Flush: Prevents shared data from leaking into personal view
        // These will be re-populated by new listeners in App.tsx
        tasks: [],
        personalEvents: [],    // Clear personal events on context switch
        householdEvents: [],   // Clear household events on context switch
        customEvents: [],      // Clear custom events on context switch
        inventory: [],
        contacts: [],
        maintenanceTasks: [],
        auditLogs: []
      };
    }

    case 'ADD_HOUSEHOLD': {
      // Prevent duplicates when adding a new household
      const householdExists = state.households.some(h => h.id === action.payload.id);
      return {
        ...state,
        households: householdExists
          ? state.households.map(h => h.id === action.payload.id ? action.payload : h) // Update existing
          : [...state.households, action.payload], // Add new
      };
    }

    case 'UPDATE_HOUSEHOLD': {
      return {
        ...state,
        households: state.households.map(h =>
          h.id === action.payload.id ? { ...h, ...action.payload.updates } : h
        ),
      };
    }

    case 'REMOVE_HOUSEHOLD': {
      return {
        ...state,
        households: state.households.filter(h => h.id !== action.payload),
        currentHouseholdId: state.currentHouseholdId === action.payload ? null : state.currentHouseholdId,
      };
    }

    // --- 🛡️ SCOPE CONTROL ---
    case 'SET_DATA_SCOPE': {
      /**
       * 🔒 Privacy Guard: Users cannot set scope to 'SHARED' or 'BOTH'
       * if currentHouseholdId is null.
       */
      const validatedScope = (state.currentHouseholdId && ['SHARED', 'BOTH'].includes(action.payload))
        ? action.payload
        : 'PERSONAL'; // Force 'PERSONAL' if no household or invalid scope for no household

      if (typeof window !== 'undefined') {
        localStorage.setItem('everyday-os-active-scope', validatedScope);
      }

      return {
        ...state,
        activeDataScope: validatedScope,
        // 🧹 Context Flush: Required to re-sync listeners for the new scope
        // These will be re-populated by new listeners in App.tsx
        tasks: [],
        personalEvents: [],    // Clear personal events on scope change
        householdEvents: [],   // Clear household events on scope change
        customEvents: [],      // Clear custom events on scope change
        inventory: [],
        contacts: [],
        maintenanceTasks: [],
      };
    }


    // --- 🛠️ FEATURE ENGINE DATA - TASKS ---
    case 'SET_TASKS':
      return {
        ...state,
        tasks: action.payload
      };

    case 'ADD_TASK': {
      const existingTask = state.tasks.find(task => task.id === action.payload.id);
      let newTasks = existingTask
          ? state.tasks.map(task => task.id === action.payload.id ? action.payload : task) // Update existing
          : [...state.tasks, action.payload]; // Add if new

      let newInventory = state.inventory;
      let newMaintenanceTasks = state.maintenanceTasks;

      if (action.payload.templateType === TemplateType.GROCERY) {
        const existingInventoryItem = state.inventory.find(item => item.id === action.payload.id);
        newInventory = existingInventoryItem
          ? state.inventory.map(item => item.id === action.payload.id ? action.payload : item)
          : [...state.inventory, action.payload];
      } else if (action.payload.templateType === TemplateType.MAINTENANCE) {
        const existingMaintenanceItem = state.maintenanceTasks.find(maint => maint.id === action.payload.id);
        newMaintenanceTasks = existingMaintenanceItem
          ? state.maintenanceTasks.map(maint => maint.id === action.payload.id ? action.payload : maint)
          : [...state.maintenanceTasks, action.payload];
      }

      return {
        ...state,
        tasks: newTasks,
        inventory: newInventory,
        maintenanceTasks: newMaintenanceTasks,
      };
    }

    case 'UPDATE_TASK': {
      const newTasks = state.tasks.map(task =>
        task.id === action.payload.id ? { ...task, ...action.payload.updates } : task
      );
      const newInventory = state.inventory.map(item =>
        item.id === action.payload.id ? { ...item, ...action.payload.updates } : item
      );
      const newMaintenanceTasks = state.maintenanceTasks.map(maint =>
        maint.id === action.payload.id ? { ...maint, ...action.payload.updates } : maint
      );
      return {
        ...state,
        tasks: newTasks,
        inventory: newInventory,
        maintenanceTasks: newMaintenanceTasks,
      };
    }

    case 'REMOVE_TASK':
      return {
        ...state,
        tasks: state.tasks.filter(task => task.id !== action.payload),
        inventory: state.inventory.filter(item => item.id !== action.payload),
        maintenanceTasks: state.maintenanceTasks.filter(maint => maint.id !== action.payload),
      };

    // --- 🛠️ FEATURE ENGINE DATA - CALENDAR EVENTS ---
    case 'SET_PERSONAL_EVENTS':
      return {
        ...state,
        personalEvents: action.payload,
      };
    case 'ADD_PERSONAL_EVENT':
      // Prevent adding duplicates and ensure immutability
      const personalEventExists = state.personalEvents.some(e => e.id === action.payload.id);
      return {
        ...state,
        personalEvents: personalEventExists
          ? state.personalEvents.map(e => e.id === action.payload.id ? action.payload : e)
          : [...state.personalEvents, action.payload],
      };
    case 'UPDATE_PERSONAL_EVENT':
      return {
        ...state,
        personalEvents: state.personalEvents.map(event =>
          event.id === action.payload.id ? { ...event, ...action.payload.updates } : event
        ),
      };

    case 'SET_HOUSEHOLD_EVENTS':
      return {
        ...state,
        householdEvents: action.payload,
      };
    case 'ADD_HOUSEHOLD_EVENT':
      const householdEventExists = state.householdEvents.some(e => e.id === action.payload.id);
      return {
        ...state,
        householdEvents: householdEventExists
          ? state.householdEvents.map(e => e.id === action.payload.id ? action.payload : e)
          : [...state.householdEvents, action.payload],
      };
    case 'UPDATE_HOUSEHOLD_EVENT':
      return {
        ...state,
        householdEvents: state.householdEvents.map(event =>
          event.id === action.payload.id ? { ...event, ...action.payload.updates } : event
        ),
      };

    // There are no explicit ADD/UPDATE/SET actions for customEvents in AppAction yet.
    // Assuming customEvents will be managed either via personal/household event flows
    // (if they can have customCalendarId) or through a future dedicated action.

    // --- 🛠️ FEATURE ENGINE DATA - INVENTORY ---
    case 'SET_INVENTORY':
      return {
        ...state,
        inventory: action.payload
      };

    // --- 🛠️ FEATURE ENGINE DATA - CONTACTS ---
    case 'SET_CONTACTS':
      return {
        ...state,
        contacts: action.payload
      };

    case 'ADD_CONTACT': {
      const contactExists = state.contacts.some(c => c.id === action.payload.id);
      return {
        ...state,
        contacts: contactExists
          ? state.contacts.map(c => c.id === action.payload.id ? action.payload : c)
          : [...state.contacts, action.payload],
      };
    }

    case 'REMOVE_CONTACT': {
      return {
        ...state,
        contacts: state.contacts.filter(c => c.id !== action.payload),
      };
    }


    // --- 🛠️ FEATURE ENGINE DATA - MAINTENANCE TASKS ---
    case 'SET_MAINTENANCE_TASKS':
      return {
        ...state,
        maintenanceTasks: action.payload
      };

    // --- UI & NOTIFICATIONS ---
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload
      };

    // --- 📐 DASHBOARD & UI ---
    case 'UPDATE_LAYOUT':
      return {
        ...state,
        currentUser: state.currentUser
          ? { ...state.currentUser, dashboardLayout: action.payload }
          : null
      };

    case 'TOGGLE_THEME': {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      return { ...state, theme: newTheme };
    }

    case 'SET_THEME':
      return { ...state, theme: action.payload };

    // --- 🛰️ SYSTEM & SYNC ---
    case 'SET_SYNC':
      return {
        ...state,
        lastSyncTimestamp: action.payload
      };

    case 'SET_BOOTING':
      return {
        ...state,
        isBooting: action.payload
      };

    case 'SET_EMAIL_VERIFIED':
      return {
        ...state,
        emailVerified: action.payload
      };

    // --- ADDED: Custom Grocery Categories --- 
    case 'SAVE_CUSTOM_GROCERY_CATEGORIES': {
      const { scope, categories } = action.payload;
      return {
        ...state,
        customGroceryCategories: {
          ...state.customGroceryCategories,
          [scope]: categories,
        },
      };
    }

    // ADDED: SET_CUSTOM_GROCERY_CATEGORIES (for loading from Firestore)
    case 'SET_CUSTOM_GROCERY_CATEGORIES': {
        const { scope, categories } = action.payload;
        return {
            ...state,
            customGroceryCategories: {
                ...state.customGroceryCategories,
                [scope]: categories,
            },
        };
    }

    case 'UPDATE_ACCESSIBILITY':
      return {
        ...state,
        currentUser: state.currentUser
          ? { ...state.currentUser, accessibility: { ...state.currentUser.accessibility, ...action.payload } }
          : null,
      };

    case 'SET_SUBSCRIPTION_STATUS':
      return { ...state, subscriptionStatus: action.payload };

    default:
      return state;
  }
};